import { Component } from '@theme/component';

/**
 * The winter home page's ON NOW row (sections/bl-window.liquid).
 *
 * Liquid already leaves out campaigns outside their dates; a cached page can outlive an end date, so this repeats
 * the check in the browser: a card past its end is hidden, the row closes up (the next card takes the wide slot and
 * the deck's data-cards is the count left, so the columns follow), and with nothing live the whole section goes.
 * The cards are always visible; there is no entrance animation.
 * (The category tiles' line-drawing animation that used to live here left with the category row on 2026-10-20;
 * `website_develop/_review/H2/before/bl-window.js` keeps it for the kit tiles.)
 *
 * Live stock (R3, 2026-10-21): a card that links to a product and writes [n] / [sizes] carries the product
 * (data-bl-handle) and its store's locations (data-bl-locs; none = every location). Once the page is up, the
 * product's stock is asked once (assets/bl-stock.js, the Storefront API) and only the words the tokens produced
 * change: the count, the sizes, and which of the two eyebrows shows (the count or the section's sold-out line).
 * The eyebrow and the line keep the height the server gave them. No answer: the server's words stay.
 *
 * @typedef {object} BlWindowRefs
 * @property {HTMLElement} [deck] - The card row (`ul`), with data-windows (`start:end,…` in Unix seconds) and
 *   data-cards (the number of cards shown)
 *
 * @extends {Component<BlWindowRefs>}
 */
class BlWindow extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#expire();
    this.#live();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#expire();
    this.#live();
  }

  /**
   * @param {string | undefined} start
   * @param {string | undefined} end
   * @param {number} now - Unix seconds
   */
  static #isLive(start, end, now) {
    const s = Number(start) || 0;
    const e = Number(end) || 0;
    return !(s > 0 && now < s) && !(e > 0 && now >= e);
  }

  /** Hides cards whose dates have passed since the page was rendered; the first card left becomes the wide one. */
  #expire() {
    const { deck } = this.refs;
    if (!deck) return;

    const now = Date.now() / 1000;
    let changed = false;
    const items = /** @type {HTMLElement[]} */ ([...deck.querySelectorAll('.bl-deck__item')]);
    for (const item of items) {
      const live = BlWindow.#isLive(item.dataset.blStart, item.dataset.blEnd, now);
      if (item.hidden === live) {
        item.hidden = !live;
        changed = true;
      }
    }
    if (!changed) return;

    const shown = items.filter((item) => !item.hidden);
    this.hidden = shown.length === 0;
    if (!shown.length) return;

    // one wide card, and it is the first card shown; the row's columns follow the count (bl-window.css data-cards)
    for (const item of items) item.classList.toggle('bl-deck__item--wide', item === shown[0]);
    deck.dataset.cards = String(shown.length);
  }

  /** The cards' [n] / [sizes] from the product's live stock (one request per product, assets/bl-stock.js). */
  async #live() {
    const token = this.dataset.blSf || '';
    const src = this.dataset.blStockSrc || '';
    const cards = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('.bl-deck__item[data-bl-handle]')]).filter(
      (card) => card.dataset.blLive !== 'done'
    );
    if (!token || !src || !cards.length) return;
    /** @type {any} */
    let stockModule;
    try {
      stockModule = await import(src);
    } catch (error) {
      return;
    }
    await Promise.all(
      cards.map(async (card) => {
        const stock = await stockModule.productStock(card.dataset.blHandle || '', token);
        if (!stock || !stock.complete || !card.isConnected) {
          card.dataset.blLive = 'kept';
          return;
        }
        BlWindow.#paint(card, stock);
        card.dataset.blLive = 'done';
      })
    );
  }

  /**
   * Counts the card's store(s) — every location when it names none — and writes the count and the sizes.
   * @param {HTMLElement} card
   * @param {{ variants: Map<string, { title: string, sale: boolean, q: number | null, at: Map<string, { q: number }> }> }} stock
   */
  static #paint(card, stock) {
    const locs = (card.dataset.blLocs || '').split(',').filter(Boolean);
    let units = 0;
    /** @type {string[]} */
    const sizes = [];
    for (const variant of stock.variants.values()) {
      let q = 0;
      if (!locs.length) {
        // every location: what Shopify has for sale (a size whose stock is not counted counts 1 while it sells)
        q = variant.q === null ? (variant.sale ? 1 : 0) : Math.max(0, variant.q);
      } else {
        for (const loc of locs) q += (variant.at.get(loc) || { q: 0 }).q;
      }
      if (q > 0) {
        units += q;
        sizes.push(variant.title);
      }
    }

    const scope = card.querySelector('.bl-deck__scope');
    const count = card.querySelector('[data-bl-n]');
    const has = card.querySelector('[data-bl-eb="has"]');
    const out = card.querySelector('[data-bl-eb="out"]');
    if (scope instanceof HTMLElement && count && has instanceof HTMLElement && out instanceof HTMLElement) {
      BlWindow.#keepBox(scope, [
        () => {
          count.textContent = String(units);
          has.hidden = units < 1;
          out.hidden = units > 0;
        },
      ]);
    }

    const line = card.querySelector('.bl-deck__line');
    const wraps = [...card.querySelectorAll('[data-bl-sz]')];
    if (line instanceof HTMLElement && wraps.length) {
      /** @param {string} text */
      const write = (text) => () => {
        for (const wrap of wraps) {
          if (!(wrap instanceof HTMLElement)) continue;
          const list = wrap.querySelector('[data-bl-sizes]');
          if (list) list.textContent = text;
          wrap.hidden = !sizes.length;
        }
      };
      const full = sizes.join(' · ');
      const short = BlWindow.#compact(sizes);
      BlWindow.#keepBox(line, short === full ? [write(full)] : [write(full), write(short)]);
    }
  }

  /**
   * The sizes with what they share written once, for a line that would outgrow its box: "159 cm · 165 cm" →
   * "159 · 165 cm"; "Pink / XS · Pink / S" → "Pink / XS · S". Otherwise the list as it is.
   * @param {string[]} sizes
   */
  static #compact(sizes) {
    if (sizes.length < 2) return sizes.join(' · ');
    // a number and a unit ("159 cm", "155W cm", "100 mm")
    const unitOf = (/** @type {string} */ size) => (size.match(/^[\d.,]+[A-Za-z]* ([A-Za-z]+)$/) || [])[1] || '';
    const unit = unitOf(sizes[0]);
    if (unit && sizes.every((size) => unitOf(size) === unit)) {
      return sizes.map((size, i) => (i < sizes.length - 1 ? size.slice(0, -(unit.length + 1)) : size)).join(' · ');
    }
    const head = (sizes[0].match(/^[^/]+ \/ /) || [''])[0];
    if (head && sizes.every((size) => size.startsWith(head) && size.length > head.length)) {
      return sizes.map((size, i) => (i ? size.slice(head.length) : size)).join(' · ');
    }
    return sizes.join(' · ');
  }

  /**
   * Write without moving the card's words: the box keeps the height the server gave it. The writes are tried in
   * order until one fits; a text that still does not fit is cut to the lines the box had (with an ellipsis), and a
   * shorter one keeps the space. Measured before the frame is painted.
   * @param {HTMLElement} box - the eyebrow (.bl-deck__scope) or the line (.bl-deck__line)
   * @param {Array<() => void>} writes
   */
  static #keepBox(box, writes) {
    const before = box.getBoundingClientRect().height;
    let after = before;
    for (const write of writes) {
      write();
      if (!before) return;
      after = box.getBoundingClientRect().height;
      if (after < before + 0.5) break;
    }
    if (Math.abs(after - before) < 0.5) return;
    box.style.minHeight = `${before}px`;
    if (after <= before) return;
    if (box.classList.contains('bl-deck__line')) {
      // the line is a clamped box already (bl-window.css): clamp it to the lines it had
      const lineHeight = parseFloat(getComputedStyle(box).lineHeight) || before;
      box.style.setProperty('-webkit-line-clamp', String(Math.max(1, Math.round(before / lineHeight))));
      box.style.maxHeight = `${before}px`;
      return;
    }
    // the eyebrow: its words on one line, cut with an ellipsis
    for (const part of box.querySelectorAll('[data-bl-eb]')) {
      if (!(part instanceof HTMLElement) || part.hidden) continue;
      Object.assign(part.style, { display: 'block', minWidth: '0', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' });
    }
  }
}

if (!customElements.get('bl-window')) customElements.define('bl-window', BlWindow);
